/**
 * @file        range-object.js
 * @author      Eddie Roosenmaallen, eddie@kingsds.network
 *              Matthew Palma, mpalma@kingsds.network
 *              Robert Mirandola, robert@kingsds.network
 * @date        October 2018
 *              July 2022
 * This module exports classes and methods for working with RangeObjects
 *
 * RangeObject:
 *  new RangeObject(start, end[, step[, group]])
 *  new RangeObject({ start, end[, step[, group]] })
 *  new RangeObject('[object RangeObject {start},{end},{step},{group}]')
 *
 *  In the first two forms, step and group are optional, defaulting to 1. In the string form, all paramteres are required
 *
 *
 *  Methods:
 *  toString()    Returns a string descriptor, suitable to pass to a new constructor
 *  toObject()    Returns a vanilla ES object with the four parameters as properties
 *  toArray()     Resolve all values from the RangeObject, grouping if group > 1, as an array
 *  nthValue(n)   Look up a single value/group
 *  slice([start[, end]])   Resolve a selection of values from start to end (exclusive).
 *                          Same semantics as Array.prototype.slice, including negative indexes
 *  materialize(now)        Resolve all values and store as an array (eg. for random distributions)
 *
 *  Properties:
 *  length        Return the total number of values (or groups, if group > 1) generasted by the RangeObject
 *  dist          Name of the distributor function
 *  values        If present, the Distribution has been materialized
 *  materializeOnScheduler  If present and truthy, the distribution will be materialized on the scheduler before
 *                instantiating tasks
 *
 *  Examples:
 *  bob = new RangeObject(1, 10)
 *  bob.toArray() // [1,2,3,4,5,6,7,8,9,10]
 *  bob.slice(-2) // [9,10]
 *  fred = new RangeObject({ start:1, end: 1000, step: 3})
 *  fred.toArray() // [1,4,7 (...) 994, 997, 1000]
 *  fred.toObject() // { start: 1, end: 1000, step: 3, group: 1}
 *  bill = new RO.RangeObject(0, 999, 3, 3)
 *  rangeobject.js?1541083657962:42 Uncaught RangeError: Range must be divisible evenly into groups
 *      at new RangeObject (rangeobject.js?1541083657962:42)
 *      at <anonymous>:1:8
 *  bill = new RO.RangeObject(0, 998, 3, 3)
 *  bill.toArray()  // [[0, 3, 6] (...), [990, 993, 996]]
 *
 * MultiRangeObject:
 *  new MultiRangeObject(rol1, rol2, ... roln)
 *  new MultiRangeObject([rol1, rol2, ... roln])
 *  Each argument is either a RangeObject, an Array-like object (, or a Distribution object? NYI ~ER). The
 *  values generated by the MRO will be a multi-dimentional array, where each element of the array is a vector
 *  across all input ranges.
 */


/**
 * @typedef {object} RangeLike
 * @property {number} length
 * @property {number} start
 * @property {number} end
 * @property {number} nthValue
 */

// Some utility functions to make validating the parameters easier
const assert = (premise, msg) => {
  if (!premise) throw new Error(msg);
}

const assertNumeric = (val, msg) => {
  assert(typeof val === 'number', msg);
  assert(!Number.isNaN(val), msg);
  assert(Number.isFinite(val), msg);
}

const RANGEOBJ_REGEX = /^\[object RangeObject (-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(\d+|undefined)\]$/;

/** 
 * Defines a consistent interface for each of the range object types
 * to inherit from, provides some array methods.
 * @access public
 */
class SuperRangeObject {
  constructor() {
    return new Proxy(this, {
      get: (target, name) => {
        if ((typeof name === 'string' || typeof name === 'number') && Number.isInteger(parseFloat(name))) {
          return target.nthValue(parseFloat(name));
        } else {
          return target[name];
        }
      }
    });
  }

  [Symbol.iterator]() {
    let index = 0;

    return {
      next: () => ({ value: this.nthValue(index++), done: index > this.length })
    };
  }

  get length() {
    return 0;
  }

  nthValue(n) {
    throw new Error("nthValue not overridden");
  }

  toArray() {
    return this.slice()
  }

  /**
   * Generate array from range starting at value `start` and ending at value `end` via `nthValue`.
   * @param {number} [start=0] index to start slice
   * @param {number} [end] index to end slice, return rest of array if not provided.
   * @access public
   */
  slice(start, end) {
    if (typeof start === 'undefined') { start = 0 } else if (start < 0) { start += this.length }

    if (typeof end === 'undefined') { end = this.length } else if (end < 0) { end += (this.length) }

    if (end > this.length) { end = this.length }

    const arr = []
    for (let i = start; i < end; i++) { arr.push(this.nthValue(i)) }

    return arr
  }

  /**
   * Converts range to an Array and then calls `filter(...args)` on it.
   * @param  {...any} args Same args as you would pass to Array#filter
   * @access public
   */
  filter(...args) {
    return this.toArray().filter(...args);
  }
}

/**
 * Range objects are vanilla ES objects used to describe value range sets for use by `compute.for()`.
 * The range must be increasing, i.e. `start` must be less than `end`.
 * Calculations made to derive the set of numbers in a range are carried out with `BigNumber`, 
 * eg. arbitrary-precision, support. The numbers `Infinity` and `-Infinity` are not supported, and 
 * the API does not differentiate between `+0` and `-0`.
 */
/**
   * An object which represents a range of values.
   * @param {number|object} startOrObject Beginning of range, or object literal with `start` and `end` properties.
   * @param {number} end End of range
   * @param {number} [step=1] Step size in range
   * @param {number|undefined} [group] Groups in range
   * @access public
   * @extends SuperRangeObject
   * @example <caption>With implicit step size = 1.</caption>
   * let rangeObject = new RangeObject(0.5,3);
   * rangeObject.toArray();
   * // [ 0.5, 1.5, 2.5 ]
   * 
   * @example <caption>With explicit step size.</caption>
   * let rangeObject = new RangeObject(1,9,3);
   * rangeObject.toArray();
   * // [ 1, 4, 7 ]
   * 
   * @example <caption>With grouping.</caption>
   * let rangeObject = new RangeObject(1,9,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7 ] ]
   */
class RangeObject extends SuperRangeObject {
  
  constructor (start, end, step, group) {
    super();
    if (typeof start === 'string' && start.match(RANGEOBJ_REGEX)) {
      const parts = start.match(RANGEOBJ_REGEX)
      start = {
        start: parseFloat(parts[1]),
        end: parseFloat(parts[2]),
        step: parseFloat(parts[3]),
        group: (parts[4] === 'undefined'? undefined : parseFloat(parts[4]))
      }
    }

    if (typeof start === 'object') {
      this.start = start.start;
      this.end = start.end;
      this.step = start.step;
      this.group = start.group;
    } else {
      this.start = start;
      this.end = end;
      this.step = step;
      this.group = group;
    }

    assertNumeric(this.start, `Invalid start parameter "${this.start}", must be numeric and finite.`);
    assertNumeric(this.end, `Invalid end parameter "${this.end}", must be numeric and finite.`);

    // Ensure step moves in the correct direction for start-end (ie, negative if end < start)
    if (typeof this.step === 'undefined') {
      this.step = Math.sign(this.end - this.start);
    } else {
      assertNumeric(this.step, `Invalid step parameter "${this.step}", must be numeric and finite.`);
      if ((this.step === 0 && this.start !== this.end) || Math.sign(this.step) !== Math.sign(this.end - this.start)) {
        throw new Error('RangeObject step must approach end from start.');
      }
    }

    if (typeof this.group !== 'undefined') {
      // group must remain undefined if not provided because no grouping should occur if not provided.
      // As per spec, even if group is 1 it should group into arrays of 1 element
      assertNumeric(this.group, `Invalid group parameter "${this.group}", must be numeric and finite.`);
      assert(Number.isInteger(this.group), `Invalid group parameter "${this.group}", must be an integer.`);
      assert(this.group > 0, `Invalid group parameter "${this.group}", must be greater than zero.`);
    }
  }

  /**
   * @returns {boolean}
   */
  static isRangelike (r) {
    if (r instanceof RangeObject) { return true }
    if (typeof r === 'object' &&
      typeof r.length === 'number' &&
      typeof r.start === 'number' &&
      typeof r.end === 'number' &&
      typeof r.nthValue === 'function') { return true }

    return false
  }

  /**
   * @returns {boolean}
   */
  static isRangeObject(r) {
    if (r instanceof RangeObject) { return true }

    return false
  }

  /**
   * Test whether a value can be passed to the RangeObject constructor
   * @param   r               Value to test
   * @param   strict          Optional. Truthy to disallow objects which already look Rangelike
   */
  static isProtoRangelike (r, strict = true) {
    if (typeof r === 'object' &&
      typeof r.start === 'number' &&
      typeof r.end === 'number') { return true }

    if (typeof r === 'string' &&
      r.match(RANGEOBJ_REGEX)) { return true }

    if (!strict && RangeObject.isRangelike(r)) { return true }

    return false
  }

  /**
   * Create string representation of range: [object RangeObject start,end,step,group]
   * @access public
   */
  toString () {
    return `[object RangeObject ${this.start},${this.end},${this.step},${this.group}]`
  }

  /**
   * Create object literal for range with properties: start, end, step, and group.
   * @access public
   */
  toObject () {
    return {
      start: this.start,
      end: this.end,
      step: this.step,
      group: this.group
    }
  }

  /**
   * Return nth value in range
   * @param {number} n
   * @access public
   * @example
   * let rangeObject = new RangeObject(1,10,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7, 10 ] ]
   * rangeObject.nthValue(1);
   * // [ 7, 10 ]
   */
  nthValue(n) {
    /**
     * `>=` since the value at index 7 in an array that's of length 7 is outside
     * its range
     */
    if (n < 0 || n >= this.length) {
      return undefined;
    }

    if (typeof this.group !== 'undefined') {
      const start = (this.group * this.step * n) + this.start
      const arr = []

      for (let i = 0; i < this.group && i + this.group * n < this.stepCount; i++) {
        arr.push(start + (i * this.step))
      }

      return arr;
    }

    return this.start + (n * this.step);
  }

  /**
   * Return max value in range
   * @access public
   * @returns {number}
   * @example
   * let rangeObject = new RangeObject(1,10,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7, 10 ] ]
   * rangeObject.max;
   * // 10
   */
  get max () {
    if (typeof this.group === 'undefined' && this.step === 1) { return this.end }

    let tail
    if (typeof this.group === 'undefined') {
      [ tail ] = this.slice(-1)
    } else {
      [ tail ] = this.slice(-1)[0].slice(-1)
    }
    return tail
  }

  /**
   * Boolean indicating whether all groups are filled.
   * Only defined for RangeObjects that group results.
   * @access public
   * @returns {boolean}
   * @example <caption>With remainder</caption>
   * let rangeObject = new RangeObject(1,9,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7 ] ]
   * rangeObject.hasRemainder;
   * // true
   * @example <caption>Without remainder</caption>
   * let rangeObject = new RangeObject(1,10,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7, 10 ] ]
   * rangeObject.hasRemainder;
   * // true
   */
  get hasRemainder () {
    if (typeof this.group === 'undefined') { return false }
    const groups = this.stepCount / this.group;

    return (groups !== Math.floor(groups));
  }

  /**
   * Number of elements in range, or number of groups if grouped.
   * @access public
   * @returns {number}
   * @example <caption>Without grouping</caption>
   * let rangeObject = new RangeObject(1,10,3);
   * rangeObject.toArray();
   * // [ 1, 4, 7, 10 ]
   * rangeObject.length;
   * // 4
   * @example <caption>With grouping</caption>
   * let rangeObject = new RangeObject(1,9,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7 ] ]
   * rangeObject.length;
   * // 2
   */
  get length () {
    return Math.ceil(this.stepCount / (this.group || 1));
  }

  /**
   * Number of steps in range (sort of like number of elements, except grouping is no longer relevant).
   * @access public
   * @returns {number}
   * @example <caption>Without grouping</caption>
   * let rangeObject = new RangeObject(1,10,3);
   * rangeObject.toArray();
   * // [ 1, 4, 7, 10 ]
   * rangeObject.stepCount;
   * // 4
   * @example <caption>With grouping</caption>
   * let rangeObject = new RangeObject(1,9,3,2);
   * rangeObject.toArray();
   * // [ [ 1, 4 ], [ 7 ] ]
   * rangeObject.length;
   * // 3
   */
  get stepCount () {
    if (this.step === 0) return 1;
    return Math.floor(Math.abs((this.end - this.start) / this.step)) + 1;
  }
}

/**
 * A sparse range object contains many {@link RangeObject}s. The range objects are stored as arrays and then are
 * concatenated into one array in the order that they were supplied to the constructor.
 * @param {RangeObject|RangeObject[]|object} arg - First range object, or array of range objects, or object with `sparse` key containing an array of range objects.
 * @param {RangeObject} rangeObject - If first argument is a RangeObject, subsquent arguments are range objects too.
 * @access public
 * @extends SuperRangeObject
 * @example
 * r0 = new RangeObject(1,2)
 * r1 = new RangeObject(1,3)
 * sro = new SparseRangeObject(r0, r1)
 * // [ 1, 2, 1, 2, 3]
 */
class SparseRangeObject extends SuperRangeObject {
  constructor() {
    super();
    let sparse = [];

    if (arguments.length === 1 && arguments[0].ranges) 
      sparse = [...arguments[0].ranges];
    else 
      sparse = [...arguments];
    
    if (sparse instanceof SparseRangeObject)
      throw new Error('Argument is of type sparse range object');
    
    // If sparse key is passed, make sure the arguments are only the range objects (compute.for() implementation) 
    if (sparse[0].sparse)
      sparse = sparse[0].sparse;
      
    sparse.map( r =>
    {
      if (!RangeObject.isProtoRangelike(r))
        throw new Error('Argument is not of type RangeObject');
    })
      
    if (sparse[0].group)
    {
      for (let i = 0; i < sparse.length; i++)
      {
        if (sparse[i].group !== sparse[0].group)
          throw new Error('Range Object has different dimension than other range objects');
      }
    }  
    
    this.sparse = true;
    this.ranges = sparse.map(r => 
    { 
      return new RangeObject(r);
    })
    
  }
  
  /**
 * Test whether a value can be passed to the SparseRangeObject constructor
 * @param   r               Value to test
 * @param   strict          Optional. Truthy to disallow objects which already look Rangelike
 */
  static isProtoSparseRangelike (r, strict = true) 
  {
    if (typeof r === 'object' && r.sparse) { return true; }
    return false;
  }
  
  /**
   * Return nth value in range
   * @param {number} n
   * @access public
   * @example
   * let sparseRangeObject = new SparseRangeObject(1,3,1);
   * rangeObject.toArray();
   * // [ 1, 2, 3]
   * rangeObject.nthValue(1);
   * // 2
   */
  nthValue(n) 
  {
    if (n < 0 || n >= this.length) { return undefined }
    
    let count = 0;
    let rangeCount = 0;
    while (count !== n)
    {
      if (count <= n)
      {
        for (let i = 0; i < this.ranges[rangeCount].length; i++)
        {
          if (count === n)
            return this.ranges[rangeCount][i];
          else
            count++;
        }
      }
      rangeCount++;
    }
    return this.ranges[rangeCount][0];
  }
  
  /**
 * Create object literal with `sparse` property and `range` property containing array of range objects.
 */
  toObject () {
    
    const obj = {
      sparse: true,
      ranges: this.ranges
    }
    return obj;
  }
  
  get length()
  {
    let len = 0;
    this.ranges.forEach((r) => len += r.length);

    return len;
  }
  
}

/**
 * Range objects are vanilla ES objects used to describe value range sets for use by `compute.for()`. 
 * Calculations made to derive the set of numbers in a range are carried out with `BigNumber`, 
 * eg. arbitrary-precision, support. The numbers `Infinity` and `-Infinity` are not supported, and 
 * the API does not differentiate between `+0` and `-0`.
 */
class MultiRangeObject extends SuperRangeObject {
  /**
   * A multi-range object contains many {@link RangeObject}s. They are iterated over 
   * with the fastest moving index going over the right-most range object in array order. Each element
   * of a multi range is a tuple of values from constituent ranges.
   * @param {RangeObject|RangeObject[]|object} arg - First range object, or array of range objects, or object with `ranges` key containing an array of range objects.
   * @param {RangeObject} rangeObject - If first argument is a RangeObject, subsquent arguments are range objects too.
   * @access public
   * @extends SuperRangeObject
   * @example
   * r0 = new RangeObject(1,2)
   * r1 = new RangeObject(1,3)
   * mro = new MultiRangeObject(r0, r1)
   * mro.toArray()
   * // [ [ 1, 1 ], [ 1, 2 ], [ 1, 3 ], [ 2, 1 ], [ 2, 2 ], [ 2, 3 ] ]
   */
  constructor () {
    super();
    var ranges = []

    if (arguments.length === 1 && typeof arguments[0] === 'string') {
      const inputs = JSON.parse(arguments[0])
      if (Array.isArray(inputs)) {
        ranges = inputs
      } else if (inputs.ranges) {
        ranges = inputs.ranges
      } else {
        ranges = [inputs]
      }
    } else if (arguments.length === 1 && Array.isArray(arguments[0])) {
      ranges = [...arguments[0]]
    } else if (arguments.length === 1 && !!arguments[0].ranges) {
      ranges = [...arguments[0].ranges]
    } else {
      ranges = [...arguments]
    }

    this.ranges = ranges.map(r => {
      if (RangeObject.isRangelike(r)) { return r }
      if (RangeObject.isRangeObject(r)) { return r }
      if (DistributionRange.isDistribution(r)) { return r }
      if (RangeObject.isProtoRangelike(r)) { return new RangeObject(r) }
      if (DistributionRange.isProtoDistribution(r)) { return new DistributionRange(r) }

      return Array.isArray(r) ? r : [r]
    })
  }

  /**
   * Test whether a value can be passed to the MultiRangeObject constructor
   * @param   r               Value to test
   * @param   strict          Optional. Truthy to disallow objects which already look Rangelike
   */
  static isProtoMultiRangelike (r, strict = true) {
    if ((typeof r === 'object') &&
      Array.isArray(r.ranges) &&
      !r.sparse)
    {
      return true;
    }

    return false
  }

  /**
   * Create string representation of this MultiRangeObject
   * @access public
   * @example
   * "[object MultiRangeObject ' + this.ranges.length + ']"
   */
  toString () {
    return '[object MultiRangeObject ' + this.ranges.length + ']'
  }

  /**
   * Create object literal with `ranges` property containing array of range objects.
   */
  toObject () {
    return { ranges: this.ranges }
  }

  /**
   * Returns a tuple of values from the ranges given by this multi range object.
   * @param {number} n index of multi-range tuple to return
   * @access public
   * @example
   * r0 = new RangeObject(1,2)
   * r1 = new RangeObject(1,3)
   * mro = new MultiRangeObject(r0, r1)
   * mro.toArray()
   * // [ [ 1, 1 ], [ 1, 2 ], [ 1, 3 ], [ 2, 1 ], [ 2, 2 ], [ 2, 3 ] ]
   * mro.nthValue(2)
   * // [ 1, 3 ]
   */
  nthValue (n) {
    if (n < 0 || n >= this.length) { return undefined }

    const indexes = []

    for (let r = (this.ranges.length - 1); r >= 0; r--) {
      const idx = n % this.ranges[r].length

      indexes.unshift(idx)

      n -= idx
      n /= this.ranges[r].length
    }

    const values = []

    for (let i = 0; i < indexes.length; i++) {
      values[i] = Array.isArray(this.ranges[i]) ? this.ranges[i][indexes[i]] : this.ranges[i].nthValue(indexes[i])
    }

    return values
  }

  /**
   * Boolean indicating whether any of the ranges in this multi-range object has a remainder. See {@link RangeObject#hasRemainder}.
   * @access public
   * @returns {boolean}
   */
  get hasRemainder () {
    for (let r of this.ranges) {
      if (r.hasRemainder) { return true }
    }

    return false
  }

  get length () {
    let len = 1

    this.ranges.forEach((r) => len *= r.length)

    return len
  }
}

// DistributionRange object wraps a distributing function into a RangeObject-like API
// which can be dropped directly into a MultiRangeObject to generate input slices
class DistributionRange extends SuperRangeObject {
  constructor (n, dist, ...params) {
    super();

    this.distributor = require('./stats-ranges').distributor;

    // If argv[0] is a string formatted as DistributionRange.toString(), then unpack it
    if (typeof n === 'string' && n.match(/^\[object DistributionRange (\w+?)\((\d+?)(?:,(.+?))?\)\]$/)) {
      const parts = n.match(/^\[object DistributionRange (\w+?)\((\d+?)(?:,(.+?))?\)\]$/)
      dist = parts[1]
      n = parseInt(parts[2])
      params = (parts[3] || '').split(',').map(e => parseFloat(e))
    }

    // If argv[0] is a string describing a DistributionRange, then unpack it
    if (typeof n === 'string' && n.match(/^(\w+?)\((\d+?)(?:,(.+?))?\)$/)) {
      const parts = n.match(/^(\w+?)\((\d+?)(?:,(.+?))?\)$/)
      dist = parts[1]
      n = parseInt(parts[2])
      params = (parts[3] || '').split(',').map(e => parseFloat(e))
    }

    // If argv[0] is a object of the right shape, then unpack it
    if (typeof n === 'object' &&
      typeof n.length === 'number' &&
      typeof n.dist === 'string' &&
      Array.isArray(n.params)) {
      // console.log('Unpacking proto-object', n)
      dist = n.dist
      params = n.params
      n = n.length
      if (Array.isArray(n.values)) { this.values = n.values }
      if (typeof n.materializeOnScheduler === 'boolean') { this.materializeOnScheduler = n.materializeOnScheduler }
    }

    Object.defineProperty(this, 'length', {
      value: n,
      enumerable: true
    })
    Object.defineProperty(this, 'dist', {
      value: dist,
      enumerable: true
    })
    Object.defineProperty(this, 'params', {
      value: params || [],
      enumerable: true
    })

    if (typeof this.distributor[dist] !== 'function') {
      // console.log({n,dist,params})
      throw new TypeError('dist param must point to an exported distributing function')
    }
  }

  /**
   * @returns {boolean}
   */
  static isDistribution (d) {
    return d instanceof DistributionRange
  }

  static isDistributionLike (d) {
    if (DistributionRange.isDistribution(d)) { return true }
    if (typeof d === 'object' &&
      typeof d.nthValue === 'function' &&
      typeof d.slice === 'function') { return true }

    return false
  }

  static isProtoDistribution (d) {
    if (typeof d === 'string' && d.match(/^\[object DistributionRange (\w+?)\((\d+?)(?:,(.+?))?\)\]$/)) { return true }
    if (typeof d === 'string' && d.match(/^(\w+?)\((\d+?)(?:,(.+?))?\)$/)) { return true }
    if (typeof d === 'object' &&
      typeof d.length === 'number' &&
      typeof d.dist === 'string' &&
      Array.isArray(d.params)) { return true }

    return false
  }

  toString () {
    return `[object DistributionRange ${this.dist}(${[this.length, ...this.params].join()})]`
  }

  toObject () {
    this.materialize();
    return {
      length: this.length,
      dist: this.dist,
      params: this.params,
      materializeOnScheduler: this.materializeOnScheduler || undefined,
      values: this.values || undefined
    }
  }

  nthValue (n) {
    if (n < 0 || n >= this.length) { return undefined }

    if (this.values) { return this.values[n] }

    const fn = this.distributor[this.dist]

    if (typeof fn === 'function') { return fn.apply(fn, [n, this.length, ...this.params]) }

    return undefined
  }

  /** Resolve the distribution to a static array
   *  @param    now           If false, then set a flag to materialize on the scheduler. Default: materialize now
   */
  materialize (now = true) {
    if (now === false) { return this.materializeOnScheduler = true }

    this.values = this.toArray()
  }
}

/** Rehydrate an input range from a vanilla ES5 object to an appropriate rangelike object
 *  @param  obj {Object | Iterable} an object created via `toObject` of `SuperRangeObject` or subclasses of it.
 *                                  Alternatively, an *Object* with `Symbol.iterator` defined, but not string!
 *  @return as appropriate, a RangeObject, DistributionRange, MultiRangeObject, or array
 */
function rehydrateRange (obj) {
  const { RemoteDataPattern } = require('dcp/dcp-client/remote-data-pattern');
  const { RemoteDataSet } = require('dcp/dcp-client/remote-data-set');

  if (typeof obj === 'number') {
    return obj
  }

  if (obj instanceof RangeObject ||
      obj instanceof SparseRangeObject ||
      obj instanceof MultiRangeObject ||
      obj instanceof RemoteDataSet ||
      obj instanceof RemoteDataPattern ||
      obj instanceof DistributionRange) {
    return obj;
  }

  // If obj looks like a RemoteDataSet, make one of those
  if (RemoteDataSet.isProtoRemoteDataSetLike(obj)) {
    return new RemoteDataSet(obj)
  }
  
  // If obj looks like a RemoteDataPattern, make one of those
  if (RemoteDataPattern.isProtoRemoteDataPatternLike(obj)) {
    return new RemoteDataPattern(obj.pattern, obj.sliceCount)
  }

  // If obj is an iterable object, coerce it to an array
  if (Symbol.iterator in Object(obj) && typeof obj !== 'string') {
    return Array.from(obj)
  }
  
  // If obj looks like a SparseRangeObject, make one of those
  if (SparseRangeObject.isProtoSparseRangelike(obj))
    return new SparseRangeObject(obj);

  // If obj looks like a MultiRangeObject, make one of those
  if (MultiRangeObject.isProtoMultiRangelike(obj)) {
    return new MultiRangeObject(obj)
  }

  // If obj looks rangelike, make a RangeObject
  if (RangeObject.isProtoRangelike(obj)) {
    return new RangeObject(obj)
  }

  // If obj looks like a proto-distribution, make a DistributionRange
  if (DistributionRange.isProtoDistribution(obj)) {
    return new DistributionRange(obj)
  }

  throw new TypeError(`obj cannot be cast to any supported Rangelike object: ${JSON.stringify(obj)}`)
}

exports.SuperRangeObject = SuperRangeObject;
exports.RangeObject = RangeObject;
exports.MultiRangeObject = MultiRangeObject;
exports.DistributionRange = DistributionRange;
exports.SparseRangeObject = SparseRangeObject;
exports.rehydrateRange = rehydrateRange;
